import React from "react";
import { TrendingUp, BarChart3, Zap, Server, Shield, Scale, ArrowUpRight, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ZAxis,
  Cell,
} from 'recharts';
import { useLanguage } from "../contexts/LanguageContext";

// ===== CHART DATA =====

const roiTimelineData = [
  { month: 'Jan', investment: 150, value: 80 },
  { month: 'Feb', investment: 280, value: 150 },
  { month: 'Mär', investment: 400, value: 280 },
  { month: 'Apr', investment: 500, value: 420 },
  { month: 'Mai', investment: 580, value: 580 },
  { month: 'Jun', investment: 650, value: 750 },
  { month: 'Jul', investment: 720, value: 920 },
  { month: 'Aug', investment: 780, value: 1050 },
  { month: 'Sep', investment: 830, value: 1180 },
  { month: 'Okt', investment: 880, value: 1320 },
  { month: 'Nov', investment: 920, value: 1480 },
  { month: 'Dez', investment: 950, value: 1680 },
];

const valueMatrixData = [
  { x: 85, y: 90, z: 200, name: 'Predictive Maintenance', color: '#0d9488' },
  { x: 70, y: 75, z: 150, name: 'Document AI', color: '#3b82f6' },
  { x: 55, y: 85, z: 180, name: 'Customer Analytics', color: '#f59e0b' },
  { x: 90, y: 60, z: 120, name: 'Process Automation', color: '#14b8a6' },
  { x: 40, y: 50, z: 100, name: 'Gen AI Pilot', color: '#a855f7' },
  { x: 75, y: 80, z: 160, name: 'Quality Control', color: '#22c55e' },
  { x: 60, y: 45, z: 90, name: 'Supply Chain AI', color: '#ef4444' },
];

// ===== TOOLTIP COMPONENTS =====

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-surface border border-hairline rounded-lg px-4 py-3 text-sm shadow-lg">
        <p className="text-ink font-medium mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-ink-muted">
            {entry.name}: <span className="text-ink font-medium">€{entry.value}k</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const ScatterTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-surface border border-hairline rounded-lg px-4 py-3 text-sm shadow-lg">
        <p className="text-ink font-medium mb-1">{data.name}</p>
        <p className="text-ink-muted">
          Impact: <span className="text-ink font-medium">{data.y}%</span>
        </p>
        <p className="text-ink-muted">
          Machbarkeit: <span className="text-ink font-medium">{data.x}%</span>
        </p>
      </div>
    );
  }
  return null;
};

// ===== MAIN COMPONENT =====

const ValueDashboardSection = () => {
  const { currentLanguage } = useLanguage();

  const statsCards = [
    {
      title: currentLanguage === 'de' ? 'Realisierter EBIT-Impact' : 'Realized EBIT Impact',
      value: '€1.2M',
      change: '+23%',
      icon: TrendingUp,
      color: 'text-brand-400',
      bgColor: 'bg-brand/15',
    },
    {
      title: currentLanguage === 'de' ? 'KI ROI-Faktor' : 'AI ROI Factor',
      value: '4.8x',
      change: '+0.6',
      icon: BarChart3,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: currentLanguage === 'de' ? 'Aktive Use Cases' : 'Active Use Cases',
      value: '14',
      change: '+3 Q4',
      icon: Zap,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
    },
  ];

  return (
    <section id="value-dashboard" className="py-20 md:py-28 bg-surface-sunken">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="max-w-[64ch] mb-14">
          <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-5">
            {currentLanguage === 'de' ? 'Das KI-Steuerzentrum' : 'The AI Control Center'}
          </h2>
          <p className="text-[1.05rem] leading-relaxed text-ink-muted max-w-[62ch]">
            {currentLanguage === 'de' 
              ? 'Echtzeitübersicht Ihrer KI-Investitionen und deren Wertbeitrag'
              : 'Real-time overview of your AI investments and their value contribution'
            }
          </p>
        </div>

        {/* Dashboard Container */}
        <div className="bg-surface border border-hairline rounded-xl p-5 lg:p-6 shadow-sm max-w-6xl mx-auto">
          
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {statsCards.map((stat, index) => (
              <Card key={index} className="border-hairline">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-5">
                    <div className={`p-2.5 rounded-lg ${stat.bgColor}`}>
                      <stat.icon className={`w-4 h-4 ${stat.color}`} />
                    </div>
                    <div className="flex items-center gap-1 text-green-400 text-xs font-medium bg-green-500/15 px-2 py-1 rounded-full">
                      <ArrowUpRight className="w-3 h-3" />
                      {stat.change}
                    </div>
                  </div>
                  <p className="text-ink-faint text-xs mb-1">{stat.title}</p>
                  <p className="text-2xl font-bold text-ink">{stat.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            
            {/* ROI Timeline Chart */}
            <Card className="border-hairline">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-ink flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-brand-400" />
                  {currentLanguage === 'de' ? 'ROI-Zeitverlauf' : 'ROI Timeline'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[240px]">
                  <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={100}>
                    <LineChart data={roiTimelineData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                      <XAxis 
                        dataKey="month" 
                        stroke="#6b7280" 
                        fontSize={12} 
                        tickLine={false} 
                        axisLine={false} 
                      />
                      <YAxis 
                        stroke="#6b7280" 
                        fontSize={12} 
                        tickLine={false} 
                        axisLine={false} 
                        tickFormatter={(v) => `€${v}k`} 
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Line 
                        type="monotone" 
                        dataKey="investment" 
                        stroke="#9ca3af" 
                        strokeWidth={2} 
                        strokeDasharray="5 5"
                        dot={false} 
                        name={currentLanguage === 'de' ? 'Investition' : 'Investment'} 
                      />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#0d9488" 
                        strokeWidth={3} 
                        dot={false} 
                        name={currentLanguage === 'de' ? 'Wertbeitrag' : 'Value Contribution'} 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center justify-center gap-6 mt-3">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-0.5 bg-ink-faint" style={{ borderStyle: 'dashed' }} />
                    <span className="text-xs text-ink-faint">
                      {currentLanguage === 'de' ? 'Investition' : 'Investment'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-0.5 bg-brand-600" />
                    <span className="text-xs text-ink-faint">
                      {currentLanguage === 'de' ? 'Wertbeitrag' : 'Value Contribution'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Value Matrix Chart */}
            <Card className="border-hairline">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-ink flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-brand-400" />
                  {currentLanguage === 'de' ? 'Wert-Matrix' : 'Value Matrix'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[240px]">
                  <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={100}>
                    <ScatterChart margin={{ top: 20, right: 20, bottom: 30, left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        type="number" 
                        dataKey="x" 
                        name="Machbarkeit" 
                        stroke="#6b7280" 
                        fontSize={12} 
                        tickLine={false} 
                        axisLine={false} 
                        domain={[0, 100]}
                        label={{ 
                          value: currentLanguage === 'de' ? 'Machbarkeit' : 'Feasibility', 
                          position: 'bottom', 
                          fill: '#6b7280', 
                          fontSize: 12,
                          offset: 15
                        }} 
                      />
                      <YAxis 
                        type="number" 
                        dataKey="y" 
                        name="Impact" 
                        stroke="#6b7280" 
                        fontSize={12} 
                        tickLine={false} 
                        axisLine={false} 
                        domain={[0, 100]}
                        label={{ 
                          value: 'Impact', 
                          angle: -90, 
                          position: 'insideLeft', 
                          fill: '#6b7280', 
                          fontSize: 12 
                        }} 
                      />
                      <ZAxis type="number" dataKey="z" range={[60, 200]} />
                      <Tooltip content={<ScatterTooltip />} />
                      <Scatter data={valueMatrixData} fill="#0d9488">
                        {valueMatrixData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Infrastructure Status */}
          <Card className="border-hairline bg-surface-sunken">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-brand/15">
                    <Server className="w-5 h-5 text-brand-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-ink text-sm">
                      {currentLanguage === 'de' ? 'Infrastruktur-Status' : 'Infrastructure Status'}
                    </h3>
                    <p className="text-xs text-ink-faint">
                      {currentLanguage === 'de' ? 'Souveräner Deutscher Stack (STACKIT)' : 'Sovereign German Stack (STACKIT)'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-brand-400" />
                    <span className="text-xs text-ink-muted">
                      {currentLanguage === 'de' ? '100% Souverän' : '100% Sovereign'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-brand-400" />
                    <span className="text-xs text-ink-muted">
                      {currentLanguage === 'de' ? 'DSGVO-konform' : 'GDPR compliant'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-brand-400" />
                    <span className="text-xs text-ink-muted">EU AI Act ready</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ValueDashboardSection;
